<template>
  <div class="app-calendar overflow-hidden border">
    <div class="row no-gutters">
      <!-- Top Filters -->
      <div class="col-12">
        <b-card class="mb-0 pb-0 pt-1">
          <b-row>
            <b-col md="2">
              <b-form-group label="Class">
                <v-select
                  v-model="selectedClass"
                  :options="classesOptions"
                  label="name"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select class"
                  @input="onClassChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Section">
                <v-select
                  v-model="selectedSection"
                  :options="currentSections"
                  label="section"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select section"
                  @input="onSectionChange"
                  :disabled="!selectedClass"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Subject">
                <v-select
                  v-model="selectedSubject"
                  :options="subjects"
                  label="subject"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select subject"
                  @input="onSubjectChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group label="Session">
                <v-select
                  v-model="selectedSession"
                  :options="sessions"
                  label="session"
                  :reduce="(val) => val.id"
                  :clearable="false"
                  placeholder="Select session"
                  @input="onSessionChange"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" class="d-flex align-items-end">
              <b-form-group>
                <b-button
                  variant="primary"
                  @click="fetchCalendarData"
                  :disabled="!canShowCalendar || loading"
                  class="w-100"
                >
                  <b-spinner v-if="loading" small class="mr-1" />
                  {{ loading ? 'Loading...' : 'Show Calendar' }}
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
          
          <!-- Refresh Weekly Labels Button - Only show if user has edit rights -->
          <!-- <b-row v-if="rights.edit" class="mt-2">
            <b-col cols="12" class="text-right">
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="refreshWeeklyLabels"
                :disabled="refreshingWeeklyLabels"
                class="refresh-weekly-labels-btn"
              >
                <feather-icon 
                  v-if="!refreshingWeeklyLabels" 
                  icon="RefreshCwIcon" 
                  size="14" 
                  class="mr-1" 
                />
                <b-spinner v-else small class="mr-1" />
                {{ refreshingWeeklyLabels ? 'Refreshing...' : 'Refresh Weekly Labels' }}
              </b-button>
            </b-col>
          </b-row> -->
        </b-card>
      </div>
      
      <!-- Message div asking user to select filters -->
      <div v-if="!showCalendar && !loading && !showDetails && rights.view" class="col-12">
        <b-card class="text-center py-4" style="min-height: 400px; display: flex; flex-direction: column; justify-content: center;">
          <h5 class="text-muted">Please select Class, Section, Subject and Session to get the calendar view</h5>
        </b-card>
      </div>
      
      <!-- Access Denied Message -->
      <div v-if="!rights.view" class="col-12">
        <b-card class="text-center py-4" style="min-height: 400px; display: flex; flex-direction: column; justify-content: center;">
          <h5 class="text-danger">
            <feather-icon icon="LockIcon" size="24" class="mb-2" />
            Access Denied
          </h5>
          <p class="text-muted">You don't have permission to view the calendar.</p>
        </b-card>
      </div>
      
      <!-- Calendar -->
      <div v-if="showCalendar && !showDetails" class="col-12 position-relative">
        <div class="card shadow-none border-0 mb-0 rounded-0">
         
          <div class="card-body pb-0">
            <full-calendar
              ref="refCalendar"
              :options="plannerCalendarOptions"
              class="full-calendar"
            >
              <template v-slot:eventContent="arg">
                <div class="lesson-plan-event">
                  <!-- Week Number Banner - Only show if user has view rights -->
                  <div v-if="rights.view && arg.event.extendedProps.weekNumber" 
                       :class="getWeekNumberClass(arg.event.extendedProps.weekNumber)"
                       class="event-week-banner">
                    <span class="week-label">{{ arg.event.extendedProps.weekNumber }}</span>
                  </div>
                  
                  <!-- Completion Status Indicators - Only show if user has view rights -->
                  <div v-if="rights.view && arg.event.extendedProps.plannerData && arg.event.extendedProps.plannerData.isCompleted" 
                       class="completion-indicators">
                    <!-- Green checkmark for completed (on time) -->
                    <div v-if="!isLateCompletion(arg.event.extendedProps.plannerData)" 
                         class="completion-badge success"
                         :title="`Completed on: ${formatCompletionDateForTooltip(arg.event.extendedProps.plannerData.completedOn)}`">
                      <span class="completion-icon">✓</span>
                      <span class="completion-text">Done</span>
                    </div>
                    
                    <!-- Red indicator for late completion -->
                    <div v-else 
                         class="completion-badge late"
                         :title="`Completed Late: ${formatCompletionDateForTooltip(arg.event.extendedProps.plannerData.completedOn)} (${getDaysLate(arg.event.extendedProps.plannerData)} days late)`">
                      <span class="completion-icon">⚠</span>
                      <span class="completion-text">{{ getDaysLate(arg.event.extendedProps.plannerData) }}d late</span>
                    </div>
                    
                    <!-- Completion date info -->
                    <div class="completion-date-info">
                      <small>{{ formatCompletionDateShort(arg.event.extendedProps.plannerData.completedOn) }}</small>
                    </div>
                  </div>
                  
                  <!-- Event Title - Only show if user has view rights -->
                  <div v-if="rights.view" class="event-title" :title="arg.event.title">
                    {{ arg.event.title }}
                  </div>
                  <!-- Event Days - Only show if user has view rights -->
                  <div v-if="rights.view && arg.event.extendedProps.dayFromName && arg.event.extendedProps.dayToName" 
                       class="event-days">
                    {{ arg.event.extendedProps.dayFromName }} to {{ arg.event.extendedProps.dayToName }}
                  </div>
                </div>
              </template>
            </full-calendar>
          </div>
        </div>
      </div>
      
      <!-- Calendar Details Component - Only show if user has view rights -->
      <div v-if="showDetails && rights.view" class="col-12">
        <calendar-details
          :planner-data="selectedPlannerData"
          :topics="topics"
          :subtopics="subtopics"
          :show-debug="true"
          :meta-class-name="metaClassName"
          :hide-details="hideDetails"
          :session-id="selectedSession"
          @close="closeDetails"
          @data-updated="onPlannerDataUpdated"
        />
      </div>
      
      <!-- No Data Message -->
      <div v-else-if="noDataMessage" class="col-12">
        <b-card class="text-center py-4">
          <h5 class="text-muted">{{ noDataMessage }}</h5>
        </b-card>
      </div>
      
      <!-- Lesson Plan Details Handler - Replaced with modal functionality -->
      <!-- <planner-details-handler
        v-model="isPlannerDetailsActive"
        :planner-data="selectedPlannerData"
        @close="closePlannerDetails"
      /> -->
    </div>
  </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import vSelect from "vue-select";
import { BCard, BRow, BCol, BFormGroup, BButton, BSpinner } from "bootstrap-vue";
import store from "@/store";
import { onUnmounted } from "@vue/composition-api";
import calendarStoreModule from "./calendarStoreModule";
import useCalendar from "./useCalendar";
import CalendarDetails from "./CalendarDetails.vue";
import axios from "axios";

export default {
  components: {
    FullCalendar,
    vSelect,
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    CalendarDetails,
  },
  data() {
    return {
      rights: {}, // Add rights object for access control
      classes: [],
      classesOptions: [],
      currentSections: [{ id: 0, section: "All" }],
      subjects: [],
      sessions: [],
      topics: [],
      subtopics: [],
      weeklyLabels: [], // Add weekly labels array
      selectedClass: null,
      selectedSection: 0,
      selectedSubject: null,
      selectedSession: null,
      db: this.$store.state.userData.db,
      campusID: this.$store.state.userData.cId,
      loading: false,
      refreshingWeeklyLabels: false, // Add loading state for weekly labels refresh
      showCalendar: false,
      noDataMessage: '',
      calendarEvents: [],
      // Add missing planner details handler properties
      isPlannerDetailsActive: false,
      selectedPlannerData: null,
      showDetails: false, // Added showDetails state
      metaClassName: '', // Store metaClassName from API response
      hideDetails: null, // Store hideDetails from API response
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    canShowCalendar() {
      return (
        this.selectedClass &&
        this.selectedSection !== null &&
        this.selectedSubject &&
        this.selectedSession
      );
    },
    // Override calendar options to use planner events instead of regular events
    plannerCalendarOptions() {
      // Create a new options object based on the original calendarOptions
      const options = { ...this.calendarOptions };
      
      // Override the events property to use our planner events function
      options.events = this.plannerEventsFunction;
      
      // Override eventClick to handle planner events - Only if user has view rights
      options.eventClick = async ({ event: clickedEvent }) => {
        // Check if user has view rights before allowing event click
        if (!this.rights.view) {
          this.$bvToast.toast("You don't have permission to view calendar details", {
            title: "Access Denied",
            variant: "warning",
            solid: true,
          });
          return;
        }
        
        
        // Extract planner data from the clicked event
        const plannerData = clickedEvent.extendedProps.plannerData;
        if (plannerData) {
          
          // The completion data is already included in plannerData from LoadSelected API
          // No need to fetch it separately
          this.selectedPlannerData = plannerData;
          
          // Show the details component and hide the calendar
          this.showCalendarDetails();
        } else {
        }
      };
      
      return options;
    },
  },
  watch: {
    // Watch for changes in calendar events and trigger refresh
    calendarEvents: {
      handler(newEvents) {
        this.$nextTick(() => {
          this.refetchCalendarEvents();
        });
      },
      deep: true
    },
    // Watch for changes in metaClassName
    metaClassName: {
      handler(newVal, oldVal) {
      },
      immediate: true
    },
    // Watch for changes in hideDetails
    hideDetails: {
      handler(newVal, oldVal) {
      },
      immediate: true
    }
  },
  async created() {
    // Initialize rights and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    // Check if user has view rights for this route
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    
    this.rights = this.$store.state.rights[this.$route.name];
    
    // Check if user has view rights
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    // Load data only if user has view rights
    await Promise.all([
      this.loadClasses(),
      this.loadSubjects(),
      this.loadSessions(),
      this.loadTopics(),
      this.loadSubtopics(),
    ]);
  },
  mounted() {
    // Ensure calendar is initialized before attempting any operations
    this.$nextTick(() => {
    });
  },
  methods: {
    async loadClasses() {
      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClassesAll?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.classes = await this.$store.dispatch("get", obj);
      this.classesOptions = this.classes.map((el) => el.cls);
      
      // Sort classes by sequence to display in correct order
      this.classesOptions.sort((a, b) => {
        const sequenceA = a.sequence || 0;
        const sequenceB = b.sequence || 0;
        return sequenceA - sequenceB;
      });
    },
    async loadSubjects() {
      const obj = {
        url: this.$store.state.domain + `Subjects?db=${encodeURIComponent(this.db)}&cId=${this.campusID}`,
        token: this.$store.state.userData.token,
      };
      const res = await this.$store.dispatch("get", obj);
      this.subjects = res;
    },
    async loadSessions() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${encodeURIComponent(this.db)}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data && res.data.status === "success" && Array.isArray(res.data.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }

        if (!this.selectedSession && this.sessions.length) {
          const currentSession = this.sessions.find(
            (el) => el.isCurrentSession === true
          );
          this.selectedSession = currentSession
            ? currentSession.id
            : this.sessions[this.sessions.length - 1].id;
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },
    async loadTopics() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerMainTopics/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        
        const res = await this.$store.dispatch("get", obj);
        
        if (res && res.data && Array.isArray(res.data)) {
          this.topics = res.data;
        } else if (res && Array.isArray(res)) {
          this.topics = res;
        } else {
          this.topics = [];
        }
        
      } catch (error) {
        console.error("Error loading topics:", error);
        this.topics = [];
      }
    },
    async loadSubtopics() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerSubTopics/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&page=1&pageSize=1000",
          token: this.$store.state.userData.token,
        };
        
        const res = await this.$store.dispatch("get", obj);
        
        if (res && res.data && Array.isArray(res.data)) {
          this.subtopics = res.data;
        } else if (res && Array.isArray(res)) {
          this.subtopics = res;
        } else {
          this.subtopics = [];
        }
        
      } catch (error) {
        console.error("Error loading subtopics:", error);
        this.subtopics = [];
      }
    },
    
    async loadWeeklyLabels() {
      try {
        const params = new URLSearchParams({
          pageNumber: "1",
          pageSize: "1000",
        });

        if (this.selectedSession) {
          params.set("sessionID", String(this.selectedSession));
        }

        const config = {
          method: "get",
          url:
            this.$store.state.domain +
            "PlannerWeeklyLabels/GetByPagination/" +
            this.plannerCampusID +
            "/pagination?" +
            params.toString(),
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
            db: this.$store.state.userData.db,
            "Content-Type": "application/json",
          },
        };

        const response = await axios(config);
        
        if (response.data && Array.isArray(response.data.data)) {
          this.weeklyLabels = response.data.data;
        } else if (response.data && Array.isArray(response.data)) {
          this.weeklyLabels = response.data;
        } else {
          this.weeklyLabels = [];
        }
        
        
        // Sort by date for better debugging
        const sortedLabels = [...this.weeklyLabels].sort((a, b) => 
          new Date(a.dateFrom) - new Date(b.dateFrom)
        );
        
        sortedLabels.forEach((label, index) => {
          const startDate = new Date(label.dateFrom);
          const endDate = new Date(label.dateTo);
        });
        
        // Check for overlapping date ranges
        const overlappingRanges = [];
        for (let i = 0; i < sortedLabels.length; i++) {
          for (let j = i + 1; j < sortedLabels.length; j++) {
            const label1 = sortedLabels[i];
            const label2 = sortedLabels[j];
            
            const start1 = new Date(label1.dateFrom);
            const end1 = new Date(label1.dateTo);
            const start2 = new Date(label2.dateFrom);
            const end2 = new Date(label2.dateTo);
            
            // Check for overlap
            if (start1 <= end2 && start2 <= end1) {
              overlappingRanges.push({
                label1: { title: label1.title, id: label1.id, start: start1, end: end1 },
                label2: { title: label2.title, id: label2.id, start: start2, end: end2 }
              });
            }
          }
        }
        
        if (overlappingRanges.length > 0) {
          overlappingRanges.forEach((overlap, index) => {
          });
        } else {
        }
        
        
        // Debug weekly label matching
        this.debugWeeklyLabelMatching();
      } catch (error) {
        console.error("Error loading weekly labels:", error);
        this.weeklyLabels = [];
      }
    },
    
    // Method to refresh weekly labels and update calendar
    async refreshWeeklyLabels() {
      this.refreshingWeeklyLabels = true;
      
      try {
        await this.loadWeeklyLabels();
        
        // If calendar is currently showing, refresh the events to update week titles
        if (this.showCalendar && this.calendarEvents.length > 0) {
          
          // Extract planner data from current events
          const plannerData = this.calendarEvents.map(event => event.extendedProps.plannerData);
          
          // Reprocess calendar data with updated weekly labels
          this.processCalendarData(plannerData);
          
          // Show success message
          this.$bvToast.toast("Weekly labels refreshed successfully", {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
            autoHideDelay: 3000
          });
        }
      } catch (error) {
        console.error("Error refreshing weekly labels:", error);
        this.$bvToast.toast("Failed to refresh weekly labels", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.refreshingWeeklyLabels = false;
      }
    },
    onClassChange() {
      this.currentSections = [{ id: 0, section: "All" }];
      if (this.selectedClass) {
        const classData = this.classes.find((elem) => elem.cls.id === this.selectedClass);
        if (classData && classData.sections.length > 0) {
          this.currentSections = [
            { id: 0, section: "All" },
            ...classData.sections
          ];
        }
      }
      this.selectedSection = 0; // Set to "All" by default
      this.resetCalendar();
    },
    onSectionChange() {
      this.resetCalendar();
    },
    onSubjectChange() {
      this.resetCalendar();
    },
    onSessionChange() {
      this.resetCalendar();
    },
    resetCalendar() {
      this.showCalendar = false;
      this.noDataMessage = '';
      this.calendarEvents = [];
      this.weeklyLabels = [];
    },
    // FullCalendar events function - this is what FullCalendar calls to get events
    plannerEventsFunction(info, successCallback) {
      
      // Ensure we're returning a valid array
      const eventsToReturn = Array.isArray(this.calendarEvents) ? this.calendarEvents : [];
      
      
      // Return our processed calendar events to FullCalendar
      successCallback(eventsToReturn);
    },
    async fetchCalendarData() {
      if (!this.canShowCalendar) return;
      
      this.loading = true;
      this.showCalendar = false;
      this.noDataMessage = '';
      
      try {
        await this.loadWeeklyLabels();

        const url = this.$store.state.domain +
          `PlannerDayWise/LoadSelected?db=${encodeURIComponent(this.db)}` +
          `&campusID=${this.campusID}` +
          `&classID=${this.selectedClass}` +
          `&sectionID=${this.selectedSection}` +
          `&subjectID=${this.selectedSubject}` +
          `&sessionID=${this.selectedSession || 0}` +
          `&fromPublished=true` +
          `&status=true`
          
        
        const obj = {
          url,
          token: this.$store.state.userData.token,
          returnData: true, // Add this to get the full response including metaClassName and hideDetails
        };
        
        const response = await this.$store.dispatch("get", obj);
        
        // Check for inactive status response
        if (response && response.status === 'inactive') {
          this.$bvToast.toast(response.data, {
            title: "Publication Status Inactive",
            variant: "warning",
            solid: true,
            autoHideDelay: 8000,
            toaster: 'b-toaster-top-center'
          });
          return; // Exit early if status is inactive
        }
        
        // Extract metaClassName and hideDetails from response
        
        // Check if response has the expected structure
        if (response && response.status === 'success' && response.metaClassName !== undefined) {
          this.metaClassName = response.metaClassName;
        } else if (response && response.metaClassName !== undefined) {
          this.metaClassName = response.metaClassName;
        } else {
        }
        
        // Extract hideDetails - check both root level and nested structure
        if (response && response.hideDetails !== undefined) {
          // Check if hideDetails is an object with a hideDetails property
          if (typeof response.hideDetails === 'object' && response.hideDetails !== null) {
            if (response.hideDetails.hideDetails !== undefined) {
              this.hideDetails = response.hideDetails.hideDetails;
            } else {
              this.hideDetails = response.hideDetails;
            }
          } else {
            this.hideDetails = response.hideDetails;
          }
        } else {
        }
        
        
        // Additional debugging for hideDetails
        
        // Handle new nested response structure
        let plannerData = [];
        
        // First, check if response has data property and it's an array
        if (response && response.data && Array.isArray(response.data)) {
          // New structure: { data: [{ plannerDayWise: {...}, plannerDayWiseDetails: [...] }] }
          
          plannerData = response.data.map((item) =>
            this.enrichPlannerItem(item)
          );
        } else if (response && Array.isArray(response)) {
          // Direct array structure
          plannerData = response.map((item) => this.enrichPlannerItem(item));
        } else if (response && typeof response === 'object') {
          // Handle array-like object with numeric keys
          
          // Check if response has data array
          if (response.data && Array.isArray(response.data)) {
            plannerData = response.data.map((item) =>
              this.enrichPlannerItem(item)
            );
          } else {
            const keys = Object.keys(response).filter(key => !isNaN(key)).sort((a, b) => parseInt(a) - parseInt(b));
            
            if (keys.length > 0) {
            plannerData = keys.map((key) =>
              this.enrichPlannerItem(response[key])
            );
            }
          }
        }
        
        if (plannerData && plannerData.length > 0) {
          this.processCalendarData(plannerData);
          this.showCalendar = true;
          
          // Additional step: ensure calendar is refreshed after showing
          this.$nextTick(() => {
            setTimeout(() => {
              this.refetchCalendarEvents();
            }, 100);
          });
        } else {
          this.noDataMessage = 'No lesson plans found for the selected criteria.';
        }
        
      } catch (error) {
        console.error("Error fetching calendar data:", error);
        this.noDataMessage = 'Error loading calendar data. Please try again.';
        this.$bvToast.toast("Failed to load calendar data", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    processCalendarData(data) {
      this.calendarEvents = [];
      
      
      if (!data || data.length === 0) {
        return;
      }
      
      // Sort weekly labels by date for proper ordering
      const sortedWeeklyLabels = [...this.weeklyLabels].sort((a, b) => 
        new Date(a.dateFrom) - new Date(b.dateFrom)
      );
      
      
      // Process each planner and assign it to the appropriate weekly label
      data.forEach((plannerData) => {
        const planner = plannerData.plannerDayWise || plannerData;

        if (!planner || !planner.dtFrom || !planner.dtTo) {
          return;
        }

        const { topic: topicName, subtopic: subtopicName } =
          this.getPlannerTopicNames(plannerData);

        if (planner.dtFrom && planner.dtTo) {
          
          
          // Parse start and end dates
          const startDate = new Date(planner.dtFrom.split('T')[0]);
          const endDate = new Date(planner.dtTo.split('T')[0]);
          
          
          // Find which weekly label(s) this planner belongs to
          const matchingWeeklyLabels = this.findMatchingWeeklyLabels(startDate, endDate);
          
          if (matchingWeeklyLabels.length > 0) {
            // Create events for each matching weekly label
            matchingWeeklyLabels.forEach(weeklyLabel => {
              const weeklyEvents = this.createWeeklyLabelEvents(plannerData, startDate, endDate, topicName, subtopicName, weeklyLabel);
          this.calendarEvents.push(...weeklyEvents);
            });
          } else {
            // If no weekly label matches, create a fallback event
            const fallbackEvent = this.createFallbackEvent(plannerData, startDate, endDate, topicName, subtopicName);
            this.calendarEvents.push(fallbackEvent);
          }
        } else {
        }
      });
      
      
      // Debug: Check if events have proper titles
      this.calendarEvents.forEach((event, index) => {
      });
      
      // Trigger calendar to refetch events using the new data
      this.$nextTick(() => {
        this.refetchCalendarEvents();
      });
    },
    
    splitEventIntoDataWeeks(planner, startDate, endDate, topicName, subtopicName, weekNumberMap) {
      const weeklyEvents = [];
      
      
      // Find all weeks that this lesson plan spans
      const weeksToProcess = [];
      
      // Start from the lesson start date
      let currentDate = new Date(startDate);
      
      // Continue until we've processed the week containing the end date
      while (currentDate <= endDate) {
        // Find the start of the current week (Sunday)
        const currentWeekStart = new Date(currentDate);
        currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay());
        
        // Find the end of this week (Saturday)
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
        
        const weekKey = currentWeekStart.toISOString().split('T')[0];
        
        // Add this week if we haven't already
        if (!weeksToProcess.some(w => w.weekKey === weekKey)) {
          weeksToProcess.push({
            weekKey,
            weekStart: new Date(currentWeekStart),
            weekEnd: new Date(currentWeekEnd)
          });
          
        }
        
        // Check if this week contains the end date
        if (currentWeekStart <= endDate && endDate <= currentWeekEnd) {
          break; // We've found the week containing the end date, we can stop
        }
        
        // Move to the start of the next week
        currentDate = new Date(currentWeekEnd);
        currentDate.setDate(currentDate.getDate() + 1); // Move to next Sunday
      }
      
      // Double-check: ensure we have the week containing the end date
      const endWeekStart = new Date(endDate);
      endWeekStart.setDate(endDate.getDate() - endDate.getDay());
      const endWeekKey = endWeekStart.toISOString().split('T')[0];
      
      if (!weeksToProcess.some(w => w.weekKey === endWeekKey)) {
        const endWeekEnd = new Date(endWeekStart);
        endWeekEnd.setDate(endWeekStart.getDate() + 6);
        
        weeksToProcess.push({
          weekKey: endWeekKey,
          weekStart: new Date(endWeekStart),
          weekEnd: new Date(endWeekEnd)
        });
        
      }
      
      
      // Process each week
      weeksToProcess.forEach((week, index) => {
        const currentWeekStart = week.weekStart;
        const currentWeekEnd = week.weekEnd;
        
        // The segment starts from the later of: lesson start date or current week start
        const segmentStart = new Date(Math.max(startDate.getTime(), currentWeekStart.getTime()));
        
        // The segment ends on the earlier of: lesson end date or current week end
        const segmentEnd = new Date(Math.min(endDate.getTime(), currentWeekEnd.getTime()));
        
        
        // Only create segment if this week actually contains lesson data
        if (segmentStart <= segmentEnd) {
          // Get the week number for this week from our mapping
          const weekNumberValue = weekNumberMap.get(week.weekKey);
          
          // Add error handling for undefined week numbers
          if (weekNumberValue === undefined) {
            console.error(`Week number not found for key: ${week.weekKey}`, {
              currentWeekStart: currentWeekStart.toDateString(),
              availableKeys: Array.from(weekNumberMap.keys()),
              plannerId: planner.id
            });
            return; // Skip this week
          }
          
          // Get dynamic week title from weekly labels
          const weekTitle = this.getWeekTitle(currentWeekStart, weekNumberValue);
        
        // Create title in format "Main Topic (Sub Topic)"
        let eventTitle = 'Daily Lesson Plan';
        if (topicName && subtopicName) {
          eventTitle = `${topicName} (${subtopicName})`;
        } else if (topicName) {
          eventTitle = topicName;
        }
        
        // Format dates for FullCalendar
          const segmentStartStr = segmentStart.toISOString().split('T')[0];
        const segmentEndStr = segmentEnd.toISOString().split('T')[0];
        
        // For FullCalendar, end date should be the day after for proper display
        const adjustedEndDate = new Date(segmentEnd);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        const adjustedEndStr = adjustedEndDate.toISOString().split('T')[0];
        
        
          // Create different colors for different events - 50 unique color palette
        const colors = [
          { bg: '#ff6b6b', border: '#ff5252', text: '#ffffff', name: 'Coral Red' },
          { bg: '#4ecdc4', border: '#26a69a', text: '#ffffff', name: 'Teal Green' },
          { bg: '#45b7d1', border: '#2196f3', text: '#ffffff', name: 'Sky Blue' },
          { bg: '#96ceb4', border: '#4caf50', text: '#ffffff', name: 'Mint Green' },
          { bg: '#feca57', border: '#ff9800', text: '#ffffff', name: 'Golden Yellow' },
          { bg: '#a29bfe', border: '#673ab7', text: '#ffffff', name: 'Purple Haze' },
          { bg: '#fd79a8', border: '#e91e63', text: '#ffffff', name: 'Pink Rose' },
          { bg: '#00b894', border: '#009688', text: '#ffffff', name: 'Emerald' },
          { bg: '#ff7675', border: '#d63031', text: '#ffffff', name: 'Strawberry' },
          { bg: '#74b9ff', border: '#0984e3', text: '#ffffff', name: 'Ocean Blue' },
          { bg: '#55a3ff', border: '#2d3436', text: '#ffffff', name: 'Electric Blue' },
            { bg: '#fd83c9', border: '#e84393', text: '#ffffff', name: 'Magenta' },
            { bg: '#00cec9', border: '#00b894', text: '#ffffff', name: 'Turquoise' },
            { bg: '#e17055', border: '#d63031', text: '#ffffff', name: 'Burnt Orange' },
            { bg: '#6c5ce7', border: '#5f3dc4', text: '#ffffff', name: 'Violet' },
            { bg: '#fdcb6e', border: '#f39c12', text: '#ffffff', name: 'Honey' },
            { bg: '#e84393', border: '#c0392b', text: '#ffffff', name: 'Hot Pink' },
            { bg: '#00b894', border: '#27ae60', text: '#ffffff', name: 'Green Sea' },
            { bg: '#0984e3', border: '#2980b9', text: '#ffffff', name: 'Dodger Blue' },
            { bg: '#9b59b6', border: '#8e44ad', text: '#ffffff', name: 'Amethyst' },
            { bg: '#f39c12', border: '#e67e22', text: '#ffffff', name: 'Orange' },
            { bg: '#1dd1a1', border: '#10ac84', text: '#ffffff', name: 'Mint' },
            { bg: '#5f27cd', border: '#341f97', text: '#ffffff', name: 'Royal Purple' },
            { bg: '#ff9ff3', border: '#f368e0', text: '#ffffff', name: 'Fuchsia' },
            { bg: '#48dbfb', border: '#0abde3', text: '#ffffff', name: 'Cyan' },
            { bg: '#ff9f43', border: '#ee5a24', text: '#ffffff', name: 'Peach' },
            { bg: '#70a1ff', border: '#5352ed', text: '#ffffff', name: 'Periwinkle' },
            { bg: '#7bed9f', border: '#2ed573', text: '#ffffff', name: 'Light Green' },
            { bg: '#ff6348', border: '#ff3742', text: '#ffffff', name: 'Tomato' },
            { bg: '#2f3542', border: '#40739e', text: '#ffffff', name: 'Dark Blue' },
            { bg: '#ff4757', border: '#c44569', text: '#ffffff', name: 'Red' },
            { bg: '#3742fa', border: '#2f3542', text: '#ffffff', name: 'Blue' },
            { bg: '#2ed573', border: '#1e90ff', text: '#ffffff', name: 'Green' },
            { bg: '#ffa502', border: '#ff6348', text: '#ffffff', name: 'Yellow Orange' },
            { bg: '#747d8c', border: '#57606f', text: '#ffffff', name: 'Gray Blue' },
            { bg: '#ff3838', border: '#ff2d92', text: '#ffffff', name: 'Neon Red' },
            { bg: '#17c0eb', border: '#0fb9b1', text: '#ffffff', name: 'Aqua' },
            { bg: '#7f8fa6', border: '#718096', text: '#ffffff', name: 'Blue Gray' },
            { bg: '#f53b57', border: '#3c40c6', text: '#ffffff', name: 'Pink Red' },
            { bg: '#00a8ff', border: '#0097e6', text: '#ffffff', name: 'Bright Blue' },
            { bg: '#9c88ff', border: '#8c7ae6', text: '#ffffff', name: 'Light Purple' },
            { bg: '#fbc531', border: '#e1b12c', text: '#ffffff', name: 'Mustard' },
            { bg: '#e55039', border: '#b33939', text: '#ffffff', name: 'Dark Red' },
            { bg: '#4834d4', border: '#686de0', text: '#ffffff', name: 'Deep Purple' },
            { bg: '#00d2d3', border: '#ff9ff3', text: '#ffffff', name: 'Teal Blue' },
            { bg: '#ff9ff3', border: '#f368e0', text: '#ffffff', name: 'Light Pink' },
            { bg: '#ffb8b8', border: '#ff3838', text: '#ffffff', name: 'Baby Pink' },
            { bg: '#c8d6e5', border: '#8395a7', text: '#ffffff', name: 'Light Blue Gray' },
            { bg: '#222f3e', border: '#576574', text: '#ffffff', name: 'Dark Gray' },
            { bg: '#ff6b35', border: '#f7931e', text: '#ffffff', name: 'Bright Orange' }
          ];
          
          // Use week number to assign colors consistently - each week gets its own color
          const colorIndex = (weekNumberValue - 1) % colors.length;
        const selectedColor = colors[colorIndex];
        
        // Determine border color based on completion status
        let borderColor = selectedColor.border;
        if (planner.isCompleted) {
          if (this.isLateCompletion(planner)) {
            borderColor = '#dc3545'; // Red for late completion
          } else {
            borderColor = '#28a745'; // Green for on-time completion
          }
        }
        
        const weeklyEvent = {
            id: `planner-${planner.id}-week-${weekNumberValue}`,
          title: eventTitle,
          start: segmentStartStr,
          end: adjustedEndStr,
          allDay: true,
          backgroundColor: selectedColor.bg,
          borderColor: borderColor,
          textColor: selectedColor.text,
          classNames: this.getEventClassNames(planner),
          extendedProps: {
            dayFromName: planner.dayFromName,
            dayToName: planner.dayToName,
            plannerData: planner,
            classID: planner.classID,
            sectionID: planner.sectionID,
            subjectID: planner.subjectID,
            mainTopicID: planner.mainTopicID,
            subtopicID: planner.subtopicID,
            topicName: topicName,
            subtopicName: subtopicName,
            originalDtFrom: planner.dtFrom,
            originalDtTo: planner.dtTo,
            weekNumber: weekTitle,
              dataWeekIndex: weekNumberValue, // Data-based week index
            segmentStart: segmentStartStr,
            segmentEnd: segmentEndStr,
            isWeekSegment: true
          }
        };
        
        weeklyEvents.push(weeklyEvent);
        } else {
        }
      });
      
      return weeklyEvents;
    },
    refetchCalendarEvents() {
      if (this.$refs.refCalendar) {
        try {
          const calendarApi = this.$refs.refCalendar.getApi();
          if (calendarApi && typeof calendarApi.refetchEvents === 'function') {
            calendarApi.refetchEvents();
          }
        } catch (error) {
          console.error("Error refetching calendar events:", error);
        }
      }
    },
    enrichPlannerItem(item = {}) {
      const planner = item.plannerDayWise || item;
      const topic =
        planner.meta_mainTopic ||
        item.meta_mainTopic ||
        item.topic ||
        planner.topic ||
        planner.metaTopicName ||
        item.metaTopicName ||
        "";
      const subtopic =
        planner.meta_subTopic ||
        item.meta_subTopic ||
        item.subTopic ||
        planner.subTopic ||
        planner.metaSubtopicName ||
        item.metaSubtopicName ||
        "";

      return {
        ...planner,
        plannerDayWiseDetails:
          item.plannerDayWiseDetails || planner.plannerDayWiseDetails || [],
        sow: item.sow || planner.sow,
        urdu: item.urdu || planner.urdu,
        meta_mainTopic: planner.meta_mainTopic || topic,
        meta_subTopic: planner.meta_subTopic || subtopic,
        topic,
        subTopic: subtopic,
        metaTopicName: topic,
        metaSubtopicName: subtopic,
      };
    },

    getPlannerTopicNames(data = {}) {
      const planner = data.plannerDayWise || data;
      const topic =
        planner.meta_mainTopic ||
        data.meta_mainTopic ||
        data.topic ||
        data.metaTopicName ||
        planner.topic ||
        planner.metaTopicName ||
        planner.mainTopicName ||
        this.getTopicName(planner.mainTopicID) ||
        "";
      const subtopic =
        planner.meta_subTopic ||
        data.meta_subTopic ||
        data.subTopic ||
        data.metaSubtopicName ||
        planner.subTopic ||
        planner.metaSubtopicName ||
        planner.subTopicName ||
        planner.subtopicName ||
        this.getSubtopicName(planner.subtopicID || planner.subTopicID) ||
        "";

      return { topic, subtopic };
    },

    getTopicName(topicID) {
      if (!topicID || !this.topics.length) {
        return '';
      }
      
      const topic = this.topics.find(t => t.id === topicID);
      const topicName = topic ? (topic.mainTopic || topic.topic || '') : '';
      
      return topicName;
    },
    getSubtopicName(subtopicID) {
      if (!subtopicID || !this.subtopics.length) {
        return '';
      }
      
      const subtopic = this.subtopics.find(s => s.id === subtopicID);
      const subtopicName = subtopic ? (subtopic.subTopic || subtopic.subtopic || '') : '';
      
      return subtopicName;
    },
    // Find the appropriate weekly label for a given date
    getWeeklyLabelForDate(date) {
      if (!this.weeklyLabels || this.weeklyLabels.length === 0) {
        return null;
      }
      
      const targetDate = new Date(date);
      targetDate.setHours(0, 0, 0, 0); // Normalize to start of day
      
      // Find all weekly labels that contain this date
      const matchingLabels = this.weeklyLabels.filter(label => {
        if (!label.dateFrom || !label.dateTo) return false;
        
        const labelStart = new Date(label.dateFrom);
        const labelEnd = new Date(label.dateTo);
        
        labelStart.setHours(0, 0, 0, 0);
        labelEnd.setHours(23, 59, 59, 999); // End of day
        
        return targetDate >= labelStart && targetDate <= labelEnd;
      });
      
      if (matchingLabels.length === 0) {
        return null;
      }
      
      // If multiple labels match, prioritize based on:
      // 1. Exact date match (start date equals target date)
      // 2. Most recent label (highest ID)
      // 3. First in the list
      let selectedLabel = matchingLabels[0];
      
      // Check for exact start date match
      const exactMatch = matchingLabels.find(label => {
        const labelStart = new Date(label.dateFrom);
        labelStart.setHours(0, 0, 0, 0);
        return targetDate.getTime() === labelStart.getTime();
      });
      
      if (exactMatch) {
        selectedLabel = exactMatch;
      } else if (matchingLabels.length > 1) {
        // If no exact match, use the label with highest ID (most recent)
        selectedLabel = matchingLabels.reduce((prev, current) => 
          (current.id > prev.id) ? current : prev
        );
      }
      
      if (matchingLabels.length > 1) {
      }
      
      return selectedLabel;
    },
    // Get week title based on weekly labels or fallback to "Week X"
    getWeekTitle(weekStartDate, weekNumber) {
      const weeklyLabel = this.getWeeklyLabelForDate(weekStartDate);
      
      if (weeklyLabel && weeklyLabel.title) {
        return weeklyLabel.title;
      }
      
      // Fallback to "Week X" if no matching weekly label found
      return `Week ${weekNumber}`;
    },
    // Debug method to test weekly label matching
    debugWeeklyLabelMatching() {
      
      // Test dates from your JSON data
      const testDates = [
        '2025-08-06', // Should match "Settlement Week"
        '2025-08-11', // Should match "Orientation/ reinforcement"
        '2025-08-18', // Should match "W1"
        '2025-08-25', // Should match "W2" (not W6 due to exact start date match)
        '2025-08-26', // Should match "W2" (not W6 due to higher ID priority)
        '2025-09-01', // Should match "W3"
        '2025-09-09', // Should match "W4"
        '2025-09-16', // Should match "W5"
      ];
      
      testDates.forEach(testDate => {
        const date = new Date(testDate);
        const label = this.getWeeklyLabelForDate(date);
      });
      
      // Test the specific W2 vs W6 scenario
      const testStartDate = new Date('2025-08-25');
      const testEndDate = new Date('2025-08-29');
      const matchingLabels = this.findMatchingWeeklyLabels(testStartDate, testEndDate);
      
      if (matchingLabels.length === 0) {
      } else {
        matchingLabels.forEach((label, index) => {
        });
      }
    },
    // Add missing planner event handlers
    handlePlannerEventClick({ event: clickedEvent }) {
      
      // Extract planner data from the clicked event
      const plannerData = clickedEvent.extendedProps.plannerData;
      if (plannerData) {
        this.selectedPlannerData = plannerData;
        this.showCalendarDetails(); // Use the correct method
      } else {
      }
    },
    closePlannerDetails() {
      this.isPlannerDetailsActive = false;
      this.selectedPlannerData = null;
    },
    // New method to show calendar details
    showCalendarDetails() {
      this.showDetails = true;
      this.showCalendar = false; // Hide main calendar when details are shown
    },
    closeDetails() {
      this.showDetails = false;
      this.showCalendar = true; // Show main calendar when details are closed
      
      // Optional: Refresh calendar events to ensure any changes are reflected
      this.$nextTick(() => {
        if (this.$refs.refCalendar) {
          this.refetchCalendarEvents();
        }
      });
    },
    
    // Calculate week number based on calendar weeks like in planner export
    calculateWeekNumber(dateString) {
      // This method is no longer used since we're using sequential numbering
      // Keeping it for compatibility but it won't be called
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      // Get the first day of the month
      const firstDayOfMonth = new Date(year, month, 1);
      
      // Get what day of the week the 1st falls on (0 = Sunday, 1 = Monday, etc.)
      const firstDayOfWeek = firstDayOfMonth.getDay();
      
      // Get the current day of the month
      const dayOfMonth = date.getDate();
      
      // Calculate the week number
      // If the month starts on Sunday (0), then days 1-7 are week 1, 8-14 are week 2, etc.
      // If the month starts on Monday (1), then day 1 is week 1, days 2-8 are week 2, etc.
      // If the month starts on Saturday (6), then day 1 is week 1, days 2-2 are week 2, etc.
      
      // The formula: week = ceil((dayOfMonth + firstDayOfWeek) / 7)
      // But we need to adjust because if firstDayOfWeek is 0 (Sunday), we don't want to add anything
      const weekNumber = Math.ceil((dayOfMonth + firstDayOfWeek) / 7);
      
      
      return `Week ${weekNumber}`;
    },
    getWeekNumberClass(weekNumber) {
      // Use a single color scheme for all weekly labels
      // This ensures consistent, professional appearance
      return 'week-color-primary';
    },
    onPlannerDataUpdated(updatedData) {
      // Check if user has edit rights before allowing data updates
      if (!this.rights.edit) {
        this.$bvToast.toast("You don't have permission to update planner data", {
          title: "Access Denied",
          variant: "warning",
          solid: true,
        });
        return;
      }
      
      
      // Update the selectedPlannerData to reflect the changes immediately
      if (this.selectedPlannerData && updatedData.plannerId === this.selectedPlannerData.id) {
        this.selectedPlannerData.isCompleted = updatedData.isCompleted;
        this.selectedPlannerData.note = updatedData.note;
        this.selectedPlannerData.completedOn = updatedData.completedOn;
      }
      
      // Update calendar events data in memory for immediate UI update
      this.calendarEvents.forEach(event => {
        if (event.extendedProps.plannerData && event.extendedProps.plannerData.id === updatedData.plannerId) {
          event.extendedProps.plannerData.isCompleted = updatedData.isCompleted;
          event.extendedProps.plannerData.note = updatedData.note;
          event.extendedProps.plannerData.completedOn = updatedData.completedOn;
          
          // Update event appearance based on completion status
          const wasCompleted = event.classNames && event.classNames.includes('completed-event');
          
          if (updatedData.isCompleted) {
            // Add completed class if not already present
            if (!wasCompleted) {
              event.classNames = [...(event.classNames || []), 'completed-event'];
            }
            
            // Check if it's a late completion and add appropriate styling
            if (this.isLateCompletion(event.extendedProps.plannerData)) {
              event.classNames = [...(event.classNames || []).filter(cls => cls !== 'completed-event'), 'completed-event', 'late-completion'];
              // Give it a slightly different border color for late completion
              event.borderColor = '#dc3545'; // Red border for late completion
            } else {
              event.classNames = [...(event.classNames || []).filter(cls => cls !== 'late-completion'), 'completed-event'];
              event.borderColor = '#28a745'; // Green border for on-time completion
            }
          } else {
            // Remove all completion-related classes
            event.classNames = (event.classNames || []).filter(cls => 
              cls !== 'completed-event' && cls !== 'late-completion'
            );
            // Restore original border color
            const originalColor = this.getOriginalBorderColor(event.extendedProps.plannerData);
            event.borderColor = originalColor;
          }
        }
      });
      
      // Refresh calendar events to show updated styling
      this.$nextTick(() => {
        this.refetchCalendarEvents();
      });
      
      // Also refresh the entire calendar data to ensure consistency with backend
      // This happens in background and ensures data integrity
      setTimeout(() => {
        this.fetchCalendarData();
      }, 1000);
    },
    
    // Helper method to get original border color based on planner ID
    getOriginalBorderColor(plannerData) {
      if (!plannerData) return '#dee2e6';
      
      const colors = [
        '#ff5252', '#26a69a', '#2196f3', '#4caf50', '#ff9800', 
        '#673ab7', '#e91e63', '#009688', '#d63031', '#0984e3', 
        '#2d3436', '#e84393'
      ];
      
      const colorIndex = plannerData.id % colors.length;
      return colors[colorIndex];
    },
    // New methods for completion status and date formatting
    isLateCompletion(plannerData) {
      if (!plannerData || !plannerData.completedOn || !plannerData.dtTo) return false;
      
      const completedOn = new Date(plannerData.completedOn);
      const endDate = new Date(plannerData.dtTo);
      
      // Remove time component for date-only comparison
      completedOn.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      return completedOn > endDate;
    },
    formatCompletionDateForTooltip(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatCompletionDateShort(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    },
    getDaysLate(plannerData) {
      if (!plannerData || !plannerData.completedOn || !plannerData.dtTo) return 0;
      
      const completedOn = new Date(plannerData.completedOn);
      const endDate = new Date(plannerData.dtTo);
      
      // Remove time component for date-only comparison
      completedOn.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (completedOn <= endDate) return 0; // Not late
      
      const diffTime = completedOn - endDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    getEventClassNames(planner) {
      const classNames = ['modern-event'];
      if (planner.isCompleted) {
        classNames.push('completed-event');
        if (this.isLateCompletion(planner)) {
          classNames.push('late-completion');
        }
      }
      return classNames;
    },
    // Find which weekly labels match a given date range
    findMatchingWeeklyLabels(startDate, endDate) {
      const matchingLabels = [];
      
      this.weeklyLabels.forEach(label => {
        if (!label.dateFrom || !label.dateTo) return;
        
        const labelStart = new Date(label.dateFrom);
        const labelEnd = new Date(label.dateTo);
        
        // Check if there's any overlap between the planner dates and weekly label dates
        if (startDate <= labelEnd && endDate >= labelStart) {
          matchingLabels.push(label);
        }
      });
      
      // If multiple labels match, prioritize based on:
      // 1. Exact start date match (planner starts exactly when weekly label starts)
      // 2. Exact end date match (planner ends exactly when weekly label ends)
      // 3. Higher ID (more recent label)
      // 4. First in the list
      if (matchingLabels.length > 1) {
        
        // Check for exact start date match
        const exactStartMatch = matchingLabels.find(label => {
          const labelStart = new Date(label.dateFrom);
          labelStart.setHours(0, 0, 0, 0);
          const plannerStart = new Date(startDate);
          plannerStart.setHours(0, 0, 0, 0);
          return plannerStart.getTime() === labelStart.getTime();
        });
        
        if (exactStartMatch) {
          return [exactStartMatch];
        }
        
        // Check for exact end date match
        const exactEndMatch = matchingLabels.find(label => {
          const labelEnd = new Date(label.dateTo);
          labelEnd.setHours(23, 59, 59, 999);
          const plannerEnd = new Date(endDate);
          plannerEnd.setHours(23, 59, 59, 999);
          return plannerEnd.getTime() === labelEnd.getTime();
        });
        
        if (exactEndMatch) {
          return [exactEndMatch];
        }
        
        // If no exact matches, use the label with highest ID (most recent)
        const highestIdLabel = matchingLabels.reduce((prev, current) => 
          (current.id > prev.id) ? current : prev
        );
        
        return [highestIdLabel];
      }
      
      return matchingLabels;
    },
    
    // Create events for a planner within a specific weekly label
    createWeeklyLabelEvents(plannerData, startDate, endDate, topicName, subtopicName, weeklyLabel) {
      const events = [];
      // Handle both data structures
      const planner = plannerData.plannerDayWise || plannerData;
      
      const labelStart = new Date(weeklyLabel.dateFrom);
      const labelEnd = new Date(weeklyLabel.dateTo);
      
      // Determine the actual segment dates (intersection of planner and weekly label)
      const segmentStart = new Date(Math.max(startDate.getTime(), labelStart.getTime()));
      const segmentEnd = new Date(Math.min(endDate.getTime(), labelEnd.getTime()));
      
      
      // Create title in format "Main Topic (Sub Topic)"
      let eventTitle = 'Daily Lesson Plan';
      
      const { topic: directTopic, subtopic: directSubtopic } =
        this.getPlannerTopicNames(plannerData);

      if (directTopic && directSubtopic) {
        eventTitle = `${directTopic} (${directSubtopic})`;
      } else if (directTopic) {
        eventTitle = directTopic;
      }

      // Format dates for FullCalendar
      const segmentStartStr = segmentStart.toISOString().split('T')[0];
      const segmentEndStr = segmentEnd.toISOString().split('T')[0];
      
      // For FullCalendar, end date should be the day after for proper display
      const adjustedEndDate = new Date(segmentEnd);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
      const adjustedEndStr = adjustedEndDate.toISOString().split('T')[0];
      
      // Get color based on weekly label
      const colorIndex = weeklyLabel.id % 50;
      const colors = [
        { bg: '#ff6b6b', border: '#ff5252', text: '#ffffff', name: 'Coral Red' },
        { bg: '#4ecdc4', border: '#26a69a', text: '#ffffff', name: 'Teal Green' },
        { bg: '#45b7d1', border: '#2196f3', text: '#ffffff', name: 'Sky Blue' },
        { bg: '#96ceb4', border: '#4caf50', text: '#ffffff', name: 'Mint Green' },
        { bg: '#feca57', border: '#ff9800', text: '#ffffff', name: 'Golden Yellow' },
        { bg: '#a29bfe', border: '#673ab7', text: '#ffffff', name: 'Purple Haze' },
        { bg: '#fd79a8', border: '#e91e63', text: '#ffffff', name: 'Pink Rose' },
        { bg: '#00b894', border: '#009688', text: '#ffffff', name: 'Emerald' },
        { bg: '#ff7675', border: '#d63031', text: '#ffffff', name: 'Strawberry' },
        { bg: '#74b9ff', border: '#0984e3', text: '#ffffff', name: 'Ocean Blue' },
        { bg: '#55a3ff', border: '#2d3436', text: '#ffffff', name: 'Electric Blue' },
        { bg: '#fd83c9', border: '#e84393', text: '#ffffff', name: 'Magenta' },
        { bg: '#00cec9', border: '#00b894', text: '#ffffff', name: 'Turquoise' },
        { bg: '#e17055', border: '#d63031', text: '#ffffff', name: 'Burnt Orange' },
        { bg: '#6c5ce7', border: '#5f3dc4', text: '#ffffff', name: 'Violet' },
        { bg: '#fdcb6e', border: '#f39c12', text: '#ffffff', name: 'Honey' },
        { bg: '#e84393', border: '#c0392b', text: '#ffffff', name: 'Hot Pink' },
        { bg: '#00b894', border: '#27ae60', text: '#ffffff', name: 'Green Sea' },
        { bg: '#0984e3', border: '#2980b9', text: '#ffffff', name: 'Dodger Blue' },
        { bg: '#9b59b6', border: '#8e44ad', text: '#ffffff', name: 'Amethyst' },
        { bg: '#f39c12', border: '#e67e22', text: '#ffffff', name: 'Orange' },
        { bg: '#1dd1a1', border: '#10ac84', text: '#ffffff', name: 'Mint' },
        { bg: '#5f27cd', border: '#341f97', text: '#ffffff', name: 'Royal Purple' },
        { bg: '#ff9ff3', border: '#f368e0', text: '#ffffff', name: 'Fuchsia' },
        { bg: '#48dbfb', border: '#0abde3', text: '#ffffff', name: 'Cyan' },
        { bg: '#ff9f43', border: '#ee5a24', text: '#ffffff', name: 'Peach' },
        { bg: '#70a1ff', border: '#5352ed', text: '#ffffff', name: 'Periwinkle' },
        { bg: '#7bed9f', border: '#2ed573', text: '#ffffff', name: 'Light Green' },
        { bg: '#ff6348', border: '#ff3742', text: '#ffffff', name: 'Tomato' },
        { bg: '#2f3542', border: '#40739e', text: '#ffffff', name: 'Dark Blue' },
        { bg: '#ff4757', border: '#c44569', text: '#ffffff', name: 'Red' },
        { bg: '#3742fa', border: '#2f3542', text: '#ffffff', name: 'Blue' },
        { bg: '#2ed573', border: '#1e90ff', text: '#ffffff', name: 'Green' },
        { bg: '#ffa502', border: '#ff6348', text: '#ffffff', name: 'Yellow Orange' },
        { bg: '#747d8c', border: '#57606f', text: '#ffffff', name: 'Gray Blue' },
        { bg: '#ff3838', border: '#ff2d92', text: '#ffffff', name: 'Neon Red' },
        { bg: '#17c0eb', border: '#0fb9b1', text: '#ffffff', name: 'Aqua' },
        { bg: '#7f8fa6', border: '#718096', text: '#ffffff', name: 'Blue Gray' },
        { bg: '#f53b57', border: '#3c40c6', text: '#ffffff', name: 'Pink Red' },
        { bg: '#00a8ff', border: '#0097e6', text: '#ffffff', name: 'Bright Blue' },
        { bg: '#9c88ff', border: '#8c7ae6', text: '#ffffff', name: 'Light Purple' },
        { bg: '#fbc531', border: '#e1b12c', text: '#ffffff', name: 'Mustard' },
        { bg: '#e55039', border: '#b33939', text: '#ffffff', name: 'Dark Red' },
        { bg: '#4834d4', border: '#686de0', text: '#ffffff', name: 'Deep Purple' },
        { bg: '#00d2d3', border: '#ff9ff3', text: '#ffffff', name: 'Teal Blue' },
        { bg: '#ff9ff3', border: '#f368e0', text: '#ffffff', name: 'Light Pink' },
        { bg: '#ffb8b8', border: '#ff3838', text: '#ffffff', name: 'Baby Pink' },
        { bg: '#c8d6e5', border: '#8395a7', text: '#ffffff', name: 'Light Blue Gray' },
        { bg: '#222f3e', border: '#576574', text: '#ffffff', name: 'Dark Gray' },
        { bg: '#ff6b35', border: '#f7931e', text: '#ffffff', name: 'Bright Orange' }
      ];
      
      const selectedColor = colors[colorIndex];
      
      // Determine border color based on completion status
      let borderColor = selectedColor.border;
      if (planner.isCompleted) {
        if (this.isLateCompletion(planner)) {
          borderColor = '#dc3545'; // Red for late completion
        } else {
          borderColor = '#28a745'; // Green for on-time completion
        }
      }
      
      const event = {
        id: `planner-${planner.id}-weekly-${weeklyLabel.id}`,
        title: eventTitle,
        start: segmentStartStr,
        end: adjustedEndStr,
        allDay: true,
        backgroundColor: selectedColor.bg,
        borderColor: borderColor,
        textColor: selectedColor.text,
        classNames: this.getEventClassNames(planner),
        extendedProps: {
          dayFromName: planner.dayFromName,
          dayToName: planner.dayToName,
          plannerData: planner,
          classID: planner.classID,
          sectionID: planner.sectionID,
          subjectID: planner.subjectID,
          mainTopicID: planner.mainTopicID,
          subtopicID: planner.subtopicID,
          topicName: topicName,
          subtopicName: subtopicName,
          originalDtFrom: planner.dtFrom,
          originalDtTo: planner.dtTo,
          weekNumber: weeklyLabel.title, // Use weekly label title instead of "Week X"
          weeklyLabelId: weeklyLabel.id,
          segmentStart: segmentStartStr,
          segmentEnd: segmentEndStr,
          isWeeklyLabelEvent: true
        }
      };
      
      events.push(event);
      return events;
    },
    
    // Create fallback event when no weekly label matches
    createFallbackEvent(plannerData, startDate, endDate, topicName, subtopicName) {
      // Handle both data structures
      const planner = plannerData.plannerDayWise || plannerData;
      
      // Create title in format "Main Topic (Sub Topic)"
      let eventTitle = 'Daily Lesson Plan';
      
      const { topic: directTopic, subtopic: directSubtopic } =
        this.getPlannerTopicNames(plannerData);

      if (directTopic && directSubtopic) {
        eventTitle = `${directTopic} (${directSubtopic})`;
      } else if (directTopic) {
        eventTitle = directTopic;
      }

      // Format dates for FullCalendar
      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];
      
      // For FullCalendar, end date should be the day after for proper display
      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
      const adjustedEndStr = adjustedEndDate.toISOString().split('T')[0];
      
      // Use a default color for fallback events
      const defaultColor = { bg: '#6c757d', border: '#495057', text: '#ffffff', name: 'Gray' };
      
      // Determine border color based on completion status
      let borderColor = defaultColor.border;
      if (planner.isCompleted) {
        if (this.isLateCompletion(planner)) {
          borderColor = '#dc3545'; // Red for late completion
        } else {
          borderColor = '#28a745'; // Green for on-time completion
        }
      }
      
      return {
        id: `planner-${planner.id}-fallback`,
        title: eventTitle,
        start: startStr,
        end: adjustedEndStr,
        allDay: true,
        backgroundColor: defaultColor.bg,
        borderColor: borderColor,
        textColor: defaultColor.text,
        classNames: this.getEventClassNames(planner),
        extendedProps: {
          dayFromName: planner.dayFromName,
          dayToName: planner.dayToName,
          plannerData: planner,
          classID: planner.classID,
          sectionID: planner.sectionID,
          subjectID: planner.subjectID,
          mainTopicID: planner.mainTopicID,
          subtopicID: planner.subtopicID,
          topicName: topicName,
          subtopicName: subtopicName,
          originalDtFrom: planner.dtFrom,
          originalDtTo: planner.dtTo,
          weekNumber: 'No Weekly Label', // Indicate no weekly label
          segmentStart: startStr,
          segmentEnd: endStr,
          isFallbackEvent: true
        }
      };
    },
  },
  setup() {
    const CALENDAR_APP_STORE_MODULE_NAME = "calendar";
    if (!store.hasModule(CALENDAR_APP_STORE_MODULE_NAME))
      store.registerModule(CALENDAR_APP_STORE_MODULE_NAME, calendarStoreModule);
    onUnmounted(() => {
      if (store.hasModule(CALENDAR_APP_STORE_MODULE_NAME))
        store.unregisterModule(CALENDAR_APP_STORE_MODULE_NAME);
    });
    const {
      refCalendar,
      calendarOptions,
    } = useCalendar();
    
    return {
      refCalendar,
      calendarOptions, // Use the original calendar options directly
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";

// Clean event styling with white text and rounded borders
.full-calendar {
  .fc-event {
    background-color: #7367f0 !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    border-radius: 8px !important;
    color: #ffffff !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
    
    // Ensure text wrapping and no overflow
    overflow: hidden !important;
    word-wrap: break-word !important;
    word-break: break-word !important;
    white-space: normal !important;
    
    // Make sure content stays within bounds
    .fc-event-main {
      overflow: hidden !important;
      padding: 2px 4px !important;
      
      .fc-event-main-frame {
        overflow: hidden !important;
        
        .fc-event-title-container {
          overflow: hidden !important;
          
          .fc-event-title {
            overflow: hidden !important;
            word-wrap: break-word !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.2 !important;
            font-size: 0.8rem !important;
            font-weight: 600 !important;
          }
        }
      }
    }
  }
  
  .lesson-plan-event {
    padding: 12px 8px 8px 8px;
    overflow: visible !important;
    position: relative;
    margin-top: 20px; /* Add more space for the week banner */
    min-height: 60px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    
    // Add completion indicators styling
    .completion-indicators {
      position: absolute;
      top: -16px;
      right: 8px;
      z-index: 20;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 2px;
      
      .completion-badge {
        display: flex;
        align-items: center;
        gap: 2px;
        padding: 2px 6px;
        border-radius: 10px;
        font-size: 0.65rem;
        font-weight: 700;
        color: #ffffff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        cursor: pointer;
        transition: all 0.2s ease;
        min-height: 18px;
        
        &.success {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          border: 1px solid rgba(255, 255, 255, 0.8);
          
          &:hover {
            background: linear-gradient(135deg, #20c997 0%, #28a745 100%);
            transform: scale(1.05);
          }
        }
        
        &.late {
          background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
          border: 1px solid rgba(255, 255, 255, 0.8);
          
          &:hover {
            background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
            transform: scale(1.05);
          }
        }
        
        .completion-icon {
          font-size: 0.7rem;
          line-height: 1;
          font-weight: 900;
        }
        
        .completion-text {
          font-size: 0.6rem;
          line-height: 1;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
      }
      
      .completion-date-info {
        background: rgba(0, 0, 0, 0.8);
        color: #ffffff;
        padding: 3px 8px;
        border-radius: 8px;
        font-size: 0.7rem;
        font-weight: 600;
        text-align: center;
        min-width: 80px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        
        small {
          font-size: 0.7rem;
          opacity: 1;
          color: #ffffff;
          font-weight: 600;
          text-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
        }
      }
    }
    
    .event-title {
      font-size: 0.85rem;
      font-weight: 600;
      color: #ffffff;
      line-height: 1.3;
      margin-bottom: 4px;
      margin-top: 6px;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      word-wrap: break-word;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
    
    .event-days {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.9);
      font-weight: 500;
      text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
      margin-top: 2px;
    }
    
    .event-week-banner {
      position: absolute;
      top: -18px;
      left: 12px;
      background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
      border-radius: 8px;
      padding: 0 16px;
      z-index: 15;
      box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1);
      text-align: center;
      min-width: 90px;
      height: 28px;
      font-size: 0.8rem;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border: 2px solid rgba(255, 255, 255, 0.95);
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(10px);
      
      /* Enhanced arrow/pointer effect */
//       &::after {
//   content: '';
//   position: absolute;
//   bottom: -10px;
//   left: 50%;
//   transform: translateX(-50%);
//   width: 0;
//   height: 0;
//   border-left: 10px solid transparent;
//   border-right: 10px solid transparent;
//   border-top: 10px solid #f7931e; /* orange fill */
//   filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));

//   /* white outline for the arrow */
//   box-shadow: 0 0 0 2px white;
// }
      
      /* Enhanced hover effects */
      &:hover {
        transform: translateY(-3px) scale(1.05);
        box-shadow: 0 8px 25px rgba(255, 107, 53, 0.6), 0 4px 15px rgba(0, 0, 0, 0.15);
        background: linear-gradient(135deg, #f7931e 0%, #ff6b35 100%);
        border-color: rgba(255, 255, 255, 1);
        
        &::after {
          border-top-color: #f7931e;
          transform: translateX(-50%) scale(1.1);
        }
      }
      
      /* Active state */
      &:active {
        transform: translateY(-1px) scale(1.02);
        transition: all 0.1s ease;
      }
      
      .week-label {
        font-size: 0.8rem;
        font-weight: 800;
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        letter-spacing: 0.5px;
        text-transform: uppercase;
        white-space: nowrap;
        margin: 0;
        padding: 0;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        position: relative;
        z-index: 2;
      }
      
      /* Add a subtle inner glow */
      &::before {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        right: 2px;
        bottom: 2px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
        border-radius: 6px;
        z-index: 1;
      }
    }
  }
}

// Refresh weekly labels button styling
.refresh-weekly-labels-btn {
  border-radius: 6px !important;
  font-weight: 500 !important;
  padding: 0.375rem 0.75rem !important;
  font-size: 0.875rem !important;
  transition: all 0.3s ease !important;
  border: 1px solid #dee2e6 !important;
  background: #ffffff !important;
  color: #6c757d !important;
  
  &:hover {
    background: #f8f9fa !important;
    border-color: #adb5bd !important;
    color: #495057 !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  &:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    transform: none !important;
    box-shadow: none !important;
  }
  
  .feather-icon {
    width: 14px;
    height: 14px;
  }
}

// Calendar header styling
.card-header {
  background: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6 !important;
  padding: 1rem 1.5rem !important;
  
  h5 {
    color: #495057 !important;
    font-weight: 600 !important;
    margin: 0 !important;
  }
}

// Primary color scheme for all weekly labels
.week-color-primary {
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%) !important;
  
  &:hover {
    background: linear-gradient(135deg, #f7931e 0%, #ff6b35 100%) !important;
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.6), 0 4px 15px rgba(0, 0, 0, 0.15) !important;
  }
  
  &::after {
    border-top-color: #f7931e !important;
  }
}
</style>
